import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
  Stack,
} from "@mui/material";
import { Visibility, VisibilityOff, Email as EmailIcon, Lock as LockIcon } from "@mui/icons-material";
import { UserType } from "../../../constants";
import { getUserDataByAuth } from "../lib/userData";
import { ROUTES } from "../../../constants";
import NewColors from "../../../shared/theme/NewColors";

const SignInPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeMessage, setNoticeMessage] = useState("");
  const [noticeSeverity, setNoticeSeverity] = useState("error");
  const [passwordResetSending, setPasswordResetSending] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("signInEmail");
    if (saved) setEmail(saved);
  }, []);

  const showError = (msg) => {
    setNoticeSeverity("error");
    setNoticeMessage(msg);
    setNoticeOpen(true);
  };

  const showSuccess = (msg) => {
    setNoticeSeverity("success");
    setNoticeMessage(msg);
    setNoticeOpen(true);
  };

  const handleForgotPassword = async () => {
    const addr = email.trim();
    if (!addr) {
      showError("Enter your email and then use Forgot password.");
      return;
    }

    // Web SDK can return auth/internal-error after Dynamic Links-related project setup changes.
    // Fall back to Identity Toolkit REST (same behavior as mighty45-client SignInScreen).
    const apiKey = firebase.app().options?.apiKey;
    if (!apiKey) {
      showError("Unable to send a password reset right now. Please try again later.");
      return;
    }

    setPasswordResetSending(true);
    try {
      await firebase.auth().sendPasswordResetEmail(addr);
      showSuccess("A password reset email was sent. Check your email.");
    } catch (err) {
      if (err?.code === "auth/internal-error") {
        try {
          const res = await fetch(
            `https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=${encodeURIComponent(apiKey)}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                requestType: "PASSWORD_RESET",
                email: addr,
              }),
            }
          );
          const data = await res.json();
          if (data.error) {
            console.error("sendPasswordResetEmail REST failed", data.error);
            showError("There was a problem sending the password reset. Try again.");
          } else {
            showSuccess("A password reset email was sent. Check your email.");
          }
        } catch (restErr) {
          console.error("sendPasswordResetEmail REST request failed", restErr);
          showError("There was a problem sending the password reset. Try again.");
        }
      } else {
        console.error("sendPasswordResetEmail failed", err);
        showError(
          err?.message || "There was a problem sending the password reset. Try again."
        );
      }
    } finally {
      setPasswordResetSending(false);
    }
  };

  const handleSignInCheck = async (credentials) => {
    await firebase.auth().currentUser.getIdTokenResult(true);
    localStorage.setItem("signInEmail", email);

    const userData = await getUserDataByAuth(credentials.user.uid);
    if (!userData || !userData.uid) {
      showError("No account found for this email. Please use the link from your coach or contact support.");
      return;
    }
    if (userData.type !== UserType.client) {
      await firebase.auth().signOut();
      showError("This account cannot access the client portal. Please use the coach console to sign in.");
      return;
    }
    if (!userData.organizationId) {
      showError("Your account is not linked to an organization. Please contact your coach.");
      return;
    }

    localStorage.setItem("uid", userData.uid);
    localStorage.setItem("credentials.user.uid", credentials.user.uid);
    localStorage.setItem("email", credentials.user.email);
    localStorage.setItem("organizationId", userData.organizationId);
    navigate(ROUTES.DASHBOARD);
  };

  const handleSignIn = async () => {
    if (!email.trim() || !password) {
      showError("Please enter your email and password.");
      return;
    }
    try {
      await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
      const credentials = await firebase.auth().signInWithEmailAndPassword(email, password);
      await handleSignInCheck(credentials);
    } catch (e) {
      showError(e.message || "Sign in failed. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: NewColors.pageBackground,
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: "100%", borderRadius: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom align="center">
            Mighty45 Client
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Sign in to view your progress and workouts
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              onKeyDown={(e) => e.key === "Enter" && handleSignIn()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSignIn}
            >
              Sign In
            </Button>
            <Button
              fullWidth
              variant="text"
              color="primary"
              size="small"
              aria-label="Forgot password"
              disabled={email.trim().length === 0 || passwordResetSending}
              onClick={handleForgotPassword}
            >
              {passwordResetSending ? "Sending…" : "Forgot password?"}
            </Button>
          </Stack>
        </CardContent>
      </Card>
      <Snackbar
        open={noticeOpen}
        autoHideDuration={6000}
        onClose={() => setNoticeOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setNoticeOpen(false)}
          severity={noticeSeverity}
          variant="filled"
        >
          {noticeMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignInPage;
